import { PROGRESS_STATUS_CODE } from "@/domains/progress/models/constants";
import type { ProgressChart } from "@/domains/progress/models/types";

type ProgressStatusCode = NonNullable<ProgressChart["status"]>["code"];

/** The one place a `status.code` decides copy: contract v1 has no server string for these. */
const STATUS_CAPTIONS: Partial<Record<ProgressStatusCode, string>> = {
  [PROGRESS_STATUS_CODE.HOLDING]:
    "Holding steady for now, and that is where your care team is focused next.",
  [PROGRESS_STATUS_CODE.WATCHING]:
    "This has been moving in the wrong direction, so your care team is looking at it first. A small plan change often turns it around, and we may adjust yours before your next refill.",
};

/** Scores carry at most one decimal, and a whole number should not read "8.0". */
export const formatProgressScore = (value: number) => {
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
};

export const buildProgressChartCaption = (chart: ProgressChart) => {
  if (chart.status === null) {
    return null;
  }

  return STATUS_CAPTIONS[chart.status.code] ?? null;
};

/** What a screen reader says in place of the drawing. */
export const buildProgressChartDescription = (chart: ProgressChart) => {
  const max = formatProgressScore(chart.scale.max);
  const reading =
    chart.latest === null || chart.latest.value === null
      ? "no reported value yet"
      : `now ${formatProgressScore(chart.latest.value)} of ${max}`;

  const clauses = [
    chart.title,
    ...(chart.baseline === null ? [] : [`started at ${formatProgressScore(chart.baseline.value)}`]),
    reading,
    ...(chart.idealLine === null
      ? []
      : [`dashed line at ${formatProgressScore(chart.idealLine.idealScore)}`]),
    ...(chart.status === null ? [] : [chart.status.label]),
  ];

  return `${clauses.join(", ")}.`;
};

/** Check-ins submitted with this question unanswered. A missing point means it was never asked. */
export const countSkippedCheckIns = (chart: ProgressChart) => {
  return chart.points.filter((point) => {
    return point.value === null;
  }).length;
};
